import { useState, useEffect } from 'react';
import ThemeToggle from './ThemeToggle';

function LandingPage({ onShowSignup, onShowLogin }) {
  const [showVideoDemo, setShowVideoDemo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    // Listen for theme changes
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    
    const handleStorageChange = () => {
      const currentTheme = localStorage.getItem('theme') || 'dark';
      setTheme(currentTheme);
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Poll for theme changes (since localStorage events don't fire in same window)
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem('theme') || 'dark';
      if (currentTheme !== theme) {
        setTheme(currentTheme);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  const industries = [
    { name: 'Construction', icon: '🏗️', color: 'from-orange-500 to-red-500' },
    { name: 'Medical', icon: '⚕️', color: 'from-red-500 to-pink-500' },
    { name: 'Retail', icon: '🛍️', color: 'from-pink-500 to-purple-500' },
    { name: 'Technology', icon: '💻', color: 'from-blue-500 to-cyan-500' },
    { name: 'Real Estate', icon: '🏘️', color: 'from-green-500 to-teal-500' },
    { name: 'Finance', icon: '💰', color: 'from-yellow-500 to-orange-500' },
    { name: 'Education', icon: '📚', color: 'from-indigo-500 to-blue-500' },
    { name: 'Food & Restaurant', icon: '🍔', color: 'from-red-500 to-orange-500' },
    { name: 'Fashion & Beauty', icon: '👗', color: 'from-pink-500 to-rose-500' },
    { name: 'Facilities Management', icon: '🏢', color: 'from-slate-500 to-gray-600' },
    { name: 'Travel & Hospitality', icon: '✈️', color: 'from-blue-500 to-teal-500' },
    { name: 'Fitness & Wellness', icon: '💪', color: 'from-green-500 to-lime-500' },
    { name: 'Legal Services', icon: '⚖️', color: 'from-gray-500 to-slate-500' },
    { name: 'E-commerce', icon: '🛒', color: 'from-purple-500 to-pink-500' },
    { name: 'Automotive', icon: '🚗', color: 'from-gray-500 to-blue-500' },
    { name: 'Entertainment', icon: '🎬', color: 'from-purple-500 to-red-500' }
  ];

  const features = [
    {
      icon: '✨',
      title: 'AI-Powered Content',
      description: 'Generate engaging social media posts using advanced AI technology'
    },
    {
      icon: '🎨',
      title: 'Image Generation',
      description: 'Create stunning, industry-specific images with Nano Banana AI'
    },
    {
      icon: '🎬',
      title: 'Video Creation',
      description: 'Produce professional videos for your social media campaigns'
    },
    {
      icon: '📱',
      title: 'Multi-Platform',
      description: 'Optimize content for Instagram, Twitter, LinkedIn & Facebook'
    },
    {
      icon: '🎯',
      title: 'Industry-Specific',
      description: 'Tailored content for 16 different industries and niches'
    },
    {
      icon: '💾',
      title: 'Save & Edit',
      description: 'Store, edit, and download your content anytime'
    }
  ];

  const pricingPlans = [
    {
      name: 'Free Trial',
      price: '$0',
      period: '/7 days',
      features: [
        '5 AI Images',
        '3 Posts',
        '1 Industry',
        'Basic Branding',
        'AI Content Generation'
      ],
      popular: false
    },
    {
      name: 'Basic',
      price: '$20',
      period: '/month',
      features: [
        '30 AI Images/month',
        '15 Posts/month',
        '1 Industry',
        'Full Branding',
        'Post Scheduling'
      ],
      popular: false
    },
    {
      name: 'Pro',
      price: '$99',
      period: '/month',
      features: [
        '100 AI Images/month',
        '50 Posts/month',
        '20 AI Videos/month',
        '2 Industries',
        'Analytics Dashboard',
        'Priority Support'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$299',
      period: '/month',
      features: [
        'Unlimited AI Images',
        'Unlimited Posts',
        'Unlimited AI Videos',
        'All 17 Industries',
        'White-label Options',
        'API Access',
        'Dedicated Support'
      ],
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Marketing Director',
      company: 'TechCorp',
      image: '👩‍💼',
      text: 'MaxeDigital AI has revolutionized our social media strategy. We create engaging content in minutes!'
    },
    {
      name: 'Michael Chen',
      role: 'Small Business Owner',
      company: 'Chen Construction',
      image: '👨‍💼',
      text: 'As a construction business, creating social content was challenging. MaxeDigital makes it effortless!'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Social Media Manager',
      company: 'Fashion Forward',
      image: '👩',
      text: 'The AI-generated images are stunning! Our engagement has increased by 300% since using MaxeDigital.'
    }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0F0F23 0%, #1E1E3F 25%, #252541 50%, #1E1E3F 75%, #0F0F23 100%)'
        : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 50%, #FFFFFF 100%)',
      color: theme === 'dark' ? '#FFFFFF' : '#111827'
    }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full backdrop-blur-md z-50 border-b transition-colors duration-300" style={{ 
        backgroundColor: theme === 'dark' ? 'rgba(15, 15, 35, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
        borderColor: theme === 'dark' ? '#3F3F5E' : '#E5E7EB'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {/* AI-Inspired Logo */}
              <div className="w-11 h-11 rounded-xl flex items-center justify-center relative" style={{
                background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 50%, #A78BFA 100%)',
                boxShadow: '0 0 25px rgba(139, 92, 246, 0.5), inset 0 0 20px rgba(255,255,255,0.1)',
              }}>
                <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Neural Network 5 Nodes with Pulse + Float */}
                  {/* Top Node */}
                  <circle cx="16" cy="9" r="4" fill="white" opacity="0.95">
                    <animate attributeName="r" values="4;4.8;4" dur="2s" repeatCount="indefinite"/>
                    <animate attributeName="cy" values="9;7;9" dur="3s" repeatCount="indefinite"/>
                    <animate attributeName="opacity" values="0.95;1;0.95" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  {/* Left Node */}
                  <circle cx="6" cy="18" r="3.5" fill="white" opacity="0.9">
                    <animate attributeName="r" values="3.5;4.2;3.5" dur="2.2s" repeatCount="indefinite" begin="0.3s"/>
                    <animate attributeName="cx" values="6;5;6" dur="2.8s" repeatCount="indefinite" begin="0.2s"/>
                    <animate attributeName="cy" values="18;16.5;18" dur="2.8s" repeatCount="indefinite" begin="0.2s"/>
                  </circle>
                  {/* Right Node */}
                  <circle cx="26" cy="18" r="3.5" fill="white" opacity="0.9">
                    <animate attributeName="r" values="3.5;4.2;3.5" dur="2.2s" repeatCount="indefinite" begin="0.6s"/>
                    <animate attributeName="cx" values="26;27;26" dur="2.8s" repeatCount="indefinite" begin="0.4s"/>
                    <animate attributeName="cy" values="18;16.5;18" dur="2.8s" repeatCount="indefinite" begin="0.4s"/>
                  </circle>
                  {/* Bottom Left Node */}
                  <circle cx="10" cy="26" r="3" fill="white" opacity="0.85">
                    <animate attributeName="r" values="3;3.6;3" dur="2.4s" repeatCount="indefinite" begin="0.9s"/>
                    <animate attributeName="cx" values="10;9;10" dur="3.2s" repeatCount="indefinite" begin="0.6s"/>
                    <animate attributeName="cy" values="26;27.5;26" dur="3.2s" repeatCount="indefinite" begin="0.6s"/>
                  </circle>
                  {/* Bottom Right Node */}
                  <circle cx="22" cy="26" r="3" fill="white" opacity="0.85">
                    <animate attributeName="r" values="3;3.6;3" dur="2.4s" repeatCount="indefinite" begin="1.2s"/>
                    <animate attributeName="cx" values="22;23;22" dur="3.2s" repeatCount="indefinite" begin="0.8s"/>
                    <animate attributeName="cy" values="26;27.5;26" dur="3.2s" repeatCount="indefinite" begin="0.8s"/>
                  </circle>
                  {/* Connection Lines */}
                  <line x1="16" y1="13" x2="8" y2="16" stroke="white" strokeWidth="1.5" opacity="0.6"/>
                  <line x1="16" y1="13" x2="24" y2="16" stroke="white" strokeWidth="1.5" opacity="0.6"/>
                  <line x1="7" y1="20" x2="10" y2="24" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                  <line x1="25" y1="20" x2="22" y2="24" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                  <line x1="10" y1="26" x2="22" y2="26" stroke="white" strokeWidth="1.5" opacity="0.4"/>
                </svg>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold" style={{
                  background: 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>MaxeDigital AI</h1>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden sm:flex items-center space-x-4">
              {/* Theme Toggle in Header */}
              <ThemeToggle />
              
              <button
                onClick={onShowLogin}
                data-testid="nav-login-button"
                className="px-6 py-2 text-white hover:text-orange-400 transition font-medium"
              >
                Login
              </button>
              <button
                onClick={onShowSignup}
                data-testid="nav-signup-button"
                className="px-6 py-2 bg-gradient-to-r accent-gradient rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition btn-glow"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 text-white hover:text-orange-400 transition"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden pb-4 border-t border-orange-500/20 mt-2">
              <div className="flex flex-col space-y-2 mt-4">
                <button
                  onClick={() => {
                    onShowLogin();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-6 py-3 text-white hover:text-orange-400 transition font-medium text-left"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    onShowSignup();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r accent-gradient rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition btn-glow text-center"
                >
                  Get Started
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-testid="hero-section">
        {/* Animated Gradient Orbs */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full filter blur-3xl" style={{
            background: theme === 'dark' 
              ? 'linear-gradient(135deg, #6B46C1, #A78BFA)' 
              : 'linear-gradient(135deg, #7C3AED, #C4B5FD)',
            animation: 'pulse 4s ease-in-out infinite'
          }}></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full filter blur-3xl" style={{
            background: theme === 'dark'
              ? 'linear-gradient(135deg, #06B6D4, #38BDF8)'
              : 'linear-gradient(135deg, #0EA5E9, #BAE6FD)',
            animation: 'pulse 5s ease-in-out infinite 1s'
          }}></div>
        </div>
        
        {/* Floating Industry & Social Media Icons - Balanced Grid Layout */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top Row - 4 icons spread across */}
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ left: '5%', top: '8%', animation: 'float 10s ease-in-out infinite', animationDelay: '0s' }}>🏗️</div>
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ left: '25%', top: '5%', animation: 'float 12s ease-in-out infinite', animationDelay: '1s' }}>💻</div>
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ right: '25%', top: '5%', animation: 'float 11s ease-in-out infinite', animationDelay: '0.5s' }}>📱</div>
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ right: '5%', top: '8%', animation: 'float 9s ease-in-out infinite', animationDelay: '1.5s' }}>⚕️</div>
          
          {/* Upper Left Side - 3 icons */}
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ left: '3%', top: '25%', animation: 'float 11s ease-in-out infinite', animationDelay: '2s' }}>🏘️</div>
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ left: '8%', top: '40%', animation: 'float 13s ease-in-out infinite', animationDelay: '0.8s' }}>💰</div>
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ left: '2%', top: '55%', animation: 'float 10s ease-in-out infinite', animationDelay: '2.5s' }}>📚</div>
          
          {/* Upper Right Side - 3 icons */}
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ right: '3%', top: '25%', animation: 'float 12s ease-in-out infinite', animationDelay: '1.2s' }}>🛍️</div>
          <div className="absolute text-3xl sm:text-4xl opacity-25" style={{ right: '8%', top: '40%', animation: 'float 10s ease-in-out infinite', animationDelay: '0.3s' }}>📸</div>
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ right: '2%', top: '55%', animation: 'float 14s ease-in-out infinite', animationDelay: '1.8s' }}>🍔</div>
          
          {/* Middle Left - 2 icons */}
          <div className="absolute text-3xl sm:text-4xl opacity-20" style={{ left: '12%', top: '70%', animation: 'float 9s ease-in-out infinite', animationDelay: '3s' }}>👗</div>
          <div className="absolute text-2xl sm:text-3xl opacity-15" style={{ left: '5%', top: '82%', animation: 'float 11s ease-in-out infinite', animationDelay: '2.2s' }}>🏢</div>
          
          {/* Middle Right - 2 icons */}
          <div className="absolute text-3xl sm:text-4xl opacity-20" style={{ right: '12%', top: '70%', animation: 'float 10s ease-in-out infinite', animationDelay: '0.6s' }}>✈️</div>
          <div className="absolute text-2xl sm:text-3xl opacity-15" style={{ right: '5%', top: '82%', animation: 'float 12s ease-in-out infinite', animationDelay: '3.5s' }}>💪</div>
          
          {/* Bottom Row - 6 icons spread across */}
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ left: '10%', bottom: '8%', animation: 'float 13s ease-in-out infinite', animationDelay: '1.3s' }}>⚖️</div>
          <div className="absolute text-3xl sm:text-4xl opacity-20" style={{ left: '25%', bottom: '5%', animation: 'float 11s ease-in-out infinite', animationDelay: '2.8s' }}>🛒</div>
          <div className="absolute text-2xl sm:text-3xl opacity-15" style={{ left: '40%', bottom: '10%', animation: 'float 10s ease-in-out infinite', animationDelay: '0.9s' }}>🌐</div>
          <div className="absolute text-2xl sm:text-3xl opacity-15" style={{ right: '40%', bottom: '10%', animation: 'float 12s ease-in-out infinite', animationDelay: '1.6s' }}>💬</div>
          <div className="absolute text-3xl sm:text-4xl opacity-20" style={{ right: '25%', bottom: '5%', animation: 'float 9s ease-in-out infinite', animationDelay: '2.1s' }}>🚗</div>
          <div className="absolute text-2xl sm:text-3xl opacity-20" style={{ right: '10%', bottom: '8%', animation: 'float 14s ease-in-out infinite', animationDelay: '0.4s' }}>🎬</div>
          
          {/* Corner accents - subtle small icons */}
          <div className="absolute text-xl opacity-10" style={{ left: '18%', top: '18%', animation: 'float 15s ease-in-out infinite', animationDelay: '4s' }}>✨</div>
          <div className="absolute text-xl opacity-10" style={{ right: '18%', top: '18%', animation: 'float 14s ease-in-out infinite', animationDelay: '3.2s' }}>🎯</div>
          <div className="absolute text-xl opacity-10" style={{ left: '20%', bottom: '18%', animation: 'float 13s ease-in-out infinite', animationDelay: '2.4s' }}>📺</div>
          <div className="absolute text-xl opacity-10" style={{ right: '20%', bottom: '18%', animation: 'float 12s ease-in-out infinite', animationDelay: '1.7s' }}>🎨</div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 fade-in-up leading-tight">
            {theme === 'dark' ? (
              <span style={{
                background: 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>AI-Powered Content</span>
            ) : (
              <span style={{ color: '#6B21A8' }}>AI-Powered Content</span>
            )}
            <br />
            <span style={{ color: theme === 'dark' ? '#FFFFFF' : '#111827' }}>For Every Industry</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto fade-in-up px-2" style={{ 
            color: theme === 'dark' ? '#D1D5DB' : '#6B7280' 
          }}>
            Create stunning social media content with AI. Posts, images, and videos tailored for your industry in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 sm:mb-12 fade-in-up px-4">
            <button
              onClick={onShowSignup}
              data-testid="hero-cta-button"
              className="w-full sm:w-auto px-8 py-4 rounded-lg font-semibold text-base sm:text-lg hover:scale-105 transition text-white"
              style={{
                background: theme === 'dark'
                  ? 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)'
                  : 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                boxShadow: theme === 'dark'
                  ? '0 0 20px rgba(139, 92, 246, 0.4)'
                  : '0 0 20px rgba(124, 58, 237, 0.4)'
              }}
            >
              Start Creating Free ✨
            </button>
            <button
              onClick={() => setShowVideoDemo(true)}
              className="w-full sm:w-auto px-8 py-4 rounded-lg font-semibold text-base sm:text-lg hover:scale-105 transition border-2 backdrop-blur-sm"
              style={{
                borderColor: theme === 'dark' ? '#8B5CF6' : '#7C3AED',
                background: theme === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(124, 58, 237, 0.1)',
                color: theme === 'dark' ? '#FFFFFF' : '#111827'
              }}
            >
              Watch Demo 🎬
            </button>
          </div>
          <div className="flex justify-center items-center space-x-8 fade-in" style={{
            color: theme === 'dark' ? '#9CA3AF' : '#6B7280'
          }}>
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ 
                color: theme === 'dark' ? '#A78BFA' : '#7C3AED'
              }}>16+</div>
              <div className="text-sm">Industries</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ 
                color: theme === 'dark' ? '#38BDF8' : '#0EA5E9'
              }}>4</div>
              <div className="text-sm">Platforms</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ 
                color: theme === 'dark' ? '#A78BFA' : '#7C3AED'
              }}>AI</div>
              <div className="text-sm">Powered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="gradient-text">Powerful Features</span>
          </h2>
          <p className="text-xl text-gray-400 text-center mb-12">Everything you need to dominate social media</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20 hover:border-orange-500/50 transition card-hover"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8" data-testid="industries-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4">
            <span className="gradient-text">17 Industries</span>
          </h2>
          <p className="text-base sm:text-xl text-gray-400 text-center mb-8 sm:mb-12 px-4">Specialized content for your niche</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                data-testid={`industry-${industry.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-6 rounded-xl text-center transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  background: theme === 'dark' 
                    ? 'linear-gradient(135deg, rgba(31, 41, 55, 1), rgba(17, 24, 39, 1))' 
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.95))',
                  border: theme === 'dark'
                    ? '1px solid rgba(249, 115, 22, 0.2)'
                    : '2px solid rgba(107, 33, 168, 0.3)',
                  boxShadow: theme === 'dark'
                    ? '0 4px 15px rgba(0, 0, 0, 0.2)'
                    : '0 4px 12px rgba(107, 33, 168, 0.15)'
                }}
              >
                <div className="text-4xl mb-3">{industry.icon}</div>
                <h3 className="text-sm font-semibold" style={{
                  color: theme === 'dark' ? '#FFFFFF' : '#1F2937'
                }}>{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">How It Works</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br accent-gradient rounded-full flex items-center justify-center text-3xl font-bold glow-effect">
                1
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Choose Industry</h3>
              <p className="text-gray-400">Select from 15 specialized industries</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br accent-gradient rounded-full flex items-center justify-center text-3xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Select Platform</h3>
              <p className="text-gray-400">Pick your social media platform</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br accent-gradient rounded-full flex items-center justify-center text-3xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Generate Content</h3>
              <p className="text-gray-400">AI creates posts, images & videos</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br accent-gradient rounded-full flex items-center justify-center text-3xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Publish & Grow</h3>
              <p className="text-gray-400">Share and watch engagement soar</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 relative" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4">
            {theme === 'dark' ? (
              <span style={{
                background: 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Simple Pricing</span>
            ) : (
              <span style={{ color: '#6B21A8' }}>Simple Pricing</span>
            )}
          </h2>
          <p className="text-base sm:text-xl text-center mb-8 sm:mb-12 px-4" style={{ 
            color: theme === 'dark' ? '#9CA3AF' : '#6B7280' 
          }}>Choose the plan that fits your needs</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border-2 transition card-hover ${
                  plan.popular
                    ? 'border-orange-500 relative'
                    : 'border-orange-500/20 hover:border-orange-500/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <span className="bg-gradient-to-r accent-gradient text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2 text-white">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold gradient-text">{plan.price}</span>
                  <span className="text-gray-400">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-300">
                      <span className="text-orange-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onShowSignup}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    plan.popular
                      ? 'bg-gradient-to-r accent-gradient hover:from-orange-600 hover:to-amber-600 btn-glow'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">What Our Users Say</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20 card-hover"
              >
                <div className="flex items-center mb-4">
                  <div className="text-4xl mr-4">{testimonial.image}</div>
                  <div>
                    <h4 className="font-bold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                    <p className="text-xs text-orange-500">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-300 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative" style={{
        background: theme === 'dark' ? 'rgba(30, 30, 63, 0.3)' : 'rgba(249, 250, 251, 0.8)',
        borderTop: theme === 'dark' ? '1px solid #3F3F5E' : '1px solid #E5E7EB'
      }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {theme === 'dark' ? (
              <span style={{
                background: 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Ready to Transform Your Social Media?</span>
            ) : (
              <span style={{ color: '#6B21A8' }}>Ready to Transform Your Social Media?</span>
            )}
          </h2>
          <p className="text-xl mb-8" style={{ 
            color: theme === 'dark' ? '#9CA3AF' : '#6B7280' 
          }}>
            Join thousands of businesses creating amazing content with AI
          </p>
          <button
            onClick={onShowSignup}
            data-testid="cta-button"
            className="px-10 py-4 rounded-lg font-semibold text-xl transition-all duration-300 hover:scale-105 text-white"
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(135deg, #6B46C1, #8B5CF6, #A78BFA)'
                : 'linear-gradient(135deg, #7C3AED, #A78BFA)',
              boxShadow: theme === 'dark'
                ? '0 0 20px rgba(139, 92, 246, 0.4)'
                : '0 0 20px rgba(124, 58, 237, 0.4)'
            }}
          >
            Start Your Free Trial 🚀
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-black py-16 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                {/* Footer AI Logo */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                  background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 50%, #A78BFA 100%)',
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)',
                }}>
                  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* 5 Nodes with Pulse + Float */}
                    <circle cx="16" cy="9" r="4" fill="white" opacity="0.95">
                      <animate attributeName="r" values="4;4.8;4" dur="2s" repeatCount="indefinite"/>
                      <animate attributeName="cy" values="9;7;9" dur="3s" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="6" cy="18" r="3.5" fill="white" opacity="0.9">
                      <animate attributeName="r" values="3.5;4.2;3.5" dur="2.2s" repeatCount="indefinite" begin="0.3s"/>
                      <animate attributeName="cx" values="6;5;6" dur="2.8s" repeatCount="indefinite" begin="0.2s"/>
                      <animate attributeName="cy" values="18;16.5;18" dur="2.8s" repeatCount="indefinite" begin="0.2s"/>
                    </circle>
                    <circle cx="26" cy="18" r="3.5" fill="white" opacity="0.9">
                      <animate attributeName="r" values="3.5;4.2;3.5" dur="2.2s" repeatCount="indefinite" begin="0.6s"/>
                      <animate attributeName="cx" values="26;27;26" dur="2.8s" repeatCount="indefinite" begin="0.4s"/>
                      <animate attributeName="cy" values="18;16.5;18" dur="2.8s" repeatCount="indefinite" begin="0.4s"/>
                    </circle>
                    <circle cx="10" cy="26" r="3" fill="white" opacity="0.85">
                      <animate attributeName="r" values="3;3.6;3" dur="2.4s" repeatCount="indefinite" begin="0.9s"/>
                      <animate attributeName="cx" values="10;9;10" dur="3.2s" repeatCount="indefinite" begin="0.6s"/>
                      <animate attributeName="cy" values="26;27.5;26" dur="3.2s" repeatCount="indefinite" begin="0.6s"/>
                    </circle>
                    <circle cx="22" cy="26" r="3" fill="white" opacity="0.85">
                      <animate attributeName="r" values="3;3.6;3" dur="2.4s" repeatCount="indefinite" begin="1.2s"/>
                      <animate attributeName="cx" values="22;23;22" dur="3.2s" repeatCount="indefinite" begin="0.8s"/>
                      <animate attributeName="cy" values="26;27.5;26" dur="3.2s" repeatCount="indefinite" begin="0.8s"/>
                    </circle>
                    {/* Connection Lines */}
                    <line x1="16" y1="13" x2="8" y2="16" stroke="white" strokeWidth="1.5" opacity="0.6"/>
                    <line x1="16" y1="13" x2="24" y2="16" stroke="white" strokeWidth="1.5" opacity="0.6"/>
                    <line x1="7" y1="20" x2="10" y2="24" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                    <line x1="25" y1="20" x2="22" y2="24" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                    <line x1="10" y1="26" x2="22" y2="26" stroke="white" strokeWidth="1.5" opacity="0.4"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold gradient-text">MaxeDigital AI</h3>
              </div>
              <p className="text-gray-400 text-sm">Empowering businesses with AI-driven social media content creation.</p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-orange-500 transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-orange-500 transition">Pricing</a></li>
                <li><a href="#industries" className="hover:text-orange-500 transition">Industries</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#about" className="hover:text-orange-500 transition">About</a></li>
                <li><a href="#blog" className="hover:text-orange-500 transition">Blog</a></li>
                <li><a href="#careers" className="hover:text-orange-500 transition">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#help" className="hover:text-orange-500 transition">Help Center</a></li>
                <li><a href="#contact" className="hover:text-orange-500 transition">Contact</a></li>
                <li><a href="#terms" className="hover:text-orange-500 transition">Terms</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2025 MaxeDigital AI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Video Demo Modal */}
      {showVideoDemo && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setShowVideoDemo(false)}>
          <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">MaxeDigital AI Demo</h3>
              <button
                onClick={() => setShowVideoDemo(false)}
                className="text-gray-400 hover:text-white text-3xl"
              >
                ×
              </button>
            </div>
            <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-4">🎬</div>
                <p className="text-gray-400">Demo video coming soon!</p>
                <p className="text-sm text-gray-500 mt-2">Experience the platform by signing up</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;
